import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  callLogsApi,
  type CreateCallLogRequest,
  type UpdateCallLogRequest,
} from "../services/callLogsApi";

interface UseCallLogsParams {
  pageNumber?: number;
  pageSize?: number;
  status?: number;
  dueFollowUpsOnly?: boolean;
  searchTerm?: string;
}

export const useCallLogs = (params?: UseCallLogsParams) => {
  const pageNumber = params?.pageNumber ?? 1;
  const pageSize = params?.pageSize ?? 20;
  const status = params?.status;
  const dueFollowUpsOnly = params?.dueFollowUpsOnly ?? false;
  const searchTerm = params?.searchTerm?.trim() ?? "";

  return useQuery({
    queryKey: ["call-logs", { pageNumber, pageSize, status, dueFollowUpsOnly, searchTerm }],
    queryFn: () =>
      callLogsApi.getAllCallLogs({
        pageNumber,
        pageSize,
        status,
        dueFollowUpsOnly,
        searchTerm: searchTerm || undefined,
      }),
  });
};

export const useCallLogById = (id?: string) => {
  return useQuery({
    queryKey: ["call-logs", "detail", id],
    queryFn: () => callLogsApi.getCallLogById(id as string),
    enabled: !!id,
  });
};

export const useCreateCallLog = () => {
  return useMutation({
    mutationFn: (payload: CreateCallLogRequest) => callLogsApi.createCallLog(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["call-logs"] });
    },
  });
};

export const useUpdateCallLog = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCallLogRequest }) =>
      callLogsApi.updateCallLog(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["call-logs"] });
      queryClient.invalidateQueries({ queryKey: ["call-logs", "detail", variables.id] });
    },
  });
};

export const useDeleteCallLog = () => {
  return useMutation({
    mutationFn: (id: string) => callLogsApi.deleteCallLog(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["call-logs"] });
    },
  });
};
